import React from "react";
import { InputDate } from "./InputDate";
import { InputNumber } from "./InputNumber";
import "./Input.scss";

type InputBaseProps = {
  /** Color variant for border and focus */
  variant?: "primary" | "secondary" | "danger";
  /** Padding and font density tier */
  density?: "compact" | "default" | "spacious";
  /** Corner radius tier (sm = canonical input radius in mixin input-like) */
  borderRadius?: "sm" | "base" | "lg";
  /** Error state */
  hasError?: boolean;
  /** Optional additional CSS class */
  className?: string;
  /** Custom steppers for type="number" (hidden native spinners) */
  numberSteppers?: boolean;
};

type InputTextProps = InputBaseProps & {
  multiline?: false;
  numberSteppers?: false;
} & React.InputHTMLAttributes<HTMLInputElement>;

type InputNumberWithSteppersProps = InputBaseProps & {
  multiline?: false;
  type: "number";
  numberSteppers: true;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">;

type InputMultilineProps = InputBaseProps & {
  multiline: true;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export type InputProps =
  | InputTextProps
  | InputNumberWithSteppersProps
  | InputMultilineProps;

const DATE_LIKE_INPUT_TYPES = new Set<string>(["date", "datetime-local", "time"]);

type DateLikeInputType = "date" | "datetime-local" | "time";

function isDateLikeInputType(type?: string): type is DateLikeInputType {
  return type !== undefined && DATE_LIKE_INPUT_TYPES.has(type);
}

/**
 * Reusable input atom. With `multiline={true}` renders a textarea.
 * With `type="number"` and `numberSteppers` renders {@link InputNumber}.
 * @component
 */
export function Input({
  className = "",
  multiline = false,
  variant = "primary",
  density = "default",
  borderRadius = "sm",
  hasError = false,
  numberSteppers = false,
  ...rest
}: InputProps): React.ReactElement {
  const baseClassName = [
    "input",
    multiline && "input--multiline",
    `input--${variant}`,
    `input--${density}`,
    borderRadius !== "sm" && `input--radius-${borderRadius}`,
    hasError && "input--error",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (multiline) {
    return (
      <textarea
        className={baseClassName}
        {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    );
  }

  const inputProps = rest as React.InputHTMLAttributes<HTMLInputElement>;
  const { type, ...inputRest } = inputProps;

  if (isDateLikeInputType(type)) {
    return (
      <InputDate
        type={type}
        className={baseClassName}
        {...inputRest}
      />
    );
  }

  if (type === "number" && numberSteppers) {
    return (
      <InputNumber
        variant={variant}
        density={density}
        borderRadius={borderRadius}
        hasError={hasError}
        className={className}
        {...inputRest}
      />
    );
  }

  return <input type={type} className={baseClassName} {...inputRest} />;
}

export default Input;
